import {useDebouncer} from '@tanstack/react-pacer'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {jsonSafeStorage} from '@github-ui/safe-storage'
import {useEffect, useEffectEvent, useMemo} from 'react'
import {DiffLineKeySchema, type DiffLineKey} from '../types'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'

const PERSIST_DEBOUNCE_MS = 200

export const PersistedCommentSchema = Type.Object(
  {
    text: Type.String(),
    shouldFocus: Type.Optional(Type.Boolean()),
  },
  // If we allow additional properties here, a value with an invalid multiline property (like `startLine: false`) would
  // pass this schema even though it should fail because it doesn't match the multiline schema
  {additionalProperties: false},
)

export type PersistedComment = Type.Static<typeof PersistedCommentSchema>

const DiffSideSchema = Type.Enum(['LEFT', 'RIGHT'])
type DiffSide = Type.Static<typeof DiffSideSchema>

const MultilineCommentArgsSchema = Type.Object({
  startLine: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
  startSide: Type.Optional(Type.Union([DiffSideSchema, Type.Null()])),
  startLineKey: Type.Optional(DiffLineKeySchema),
  endLineKey: Type.Optional(DiffLineKeySchema),
})

type MultilineCommentArgs = Type.Static<typeof MultilineCommentArgsSchema>

export const PersistedDiffCommentSchema = Type.Intersect([
  MultilineCommentArgsSchema,
  Type.Partial(PersistedCommentSchema),
])

export type PersistedDiffComment = Type.Static<typeof PersistedDiffCommentSchema>

export const StoredCommentSchema = Type.Union([PersistedCommentSchema, PersistedDiffCommentSchema])
export const StoredCommentValidator = Compile(StoredCommentSchema)

const {getItem, removeItem, setItem} = jsonSafeStorage('localStorage', StoredCommentValidator)

export const buildKey = (
  parts: {diffSide?: DiffSide; filePath: string; line?: number; subjectId: string},
  fileLevelComment: boolean,
) => {
  const {diffSide, filePath, line, subjectId} = parts
  if (fileLevelComment) {
    return `PullRequest:${subjectId}-File:${filePath}`
  }
  if (line !== undefined && diffSide !== undefined) {
    return `PullRequest:${subjectId}-File:${filePath}-Line:${line}-DiffSide:${diffSide}`
  }

  throw new Error('Cannot build diff line comment cache key for a comment without a line number or diff side')
}

interface PersistedCommentArgs extends MultilineCommentArgs {
  text: string
}

/**
 * Hook that returns whether a persisted comment exists as well as a function to remove the persisted comment from storage
 */
function usePersistedCommentDataBase<T>(localStorageKey: string, handlePersistedCommentExists?: (arg: T) => void) {
  const persistedComment = getItem(localStorageKey)

  const hasPersistedComment = !!persistedComment?.text?.trim()

  const handlePersistedCommentExistsEvent = useEffectEvent(() => {
    if (!hasPersistedComment) return
    handlePersistedCommentExists?.(persistedComment as T)
  })
  useEffect(() => {
    handlePersistedCommentExistsEvent()
  }, [hasPersistedComment])

  const removePersistedCommentFromStorage = () => {
    removeItem(localStorageKey)
  }

  return {
    hasPersistedComment,
    removePersistedCommentFromStorage,
  }
}

/**
 * Hook that returns whether a persisted comment exists
 * and functions to persist and remove pull request comment data from local storage
 *
 */
export function usePersistedCommentData({
  subjectId,
  handlePersistedCommentExists,
}: {
  subjectId: string
  handlePersistedCommentExists?: (comment: PersistedComment) => void
}) {
  const persistedCommentLocalStorageKey = useMemo(() => {
    return `Comment:${subjectId}`
  }, [subjectId])

  const {hasPersistedComment, removePersistedCommentFromStorage: removeFromStorage} =
    usePersistedCommentDataBase<PersistedComment>(persistedCommentLocalStorageKey, handlePersistedCommentExists)

  const debouncer = useDebouncer(
    (commentText: string) => {
      setItem(persistedCommentLocalStorageKey, {text: commentText})
    },
    {wait: PERSIST_DEBOUNCE_MS, onUnmount: d => d.flush()},
  )

  const debouncePersistEnabled = isFeatureEnabled('pull_requests_debounce_comment_persist')

  const persistCommentToStorage = debouncePersistEnabled
    ? (commentText: string) => debouncer.maybeExecute(commentText)
    : (commentText: string) => {
        setItem(persistedCommentLocalStorageKey, {text: commentText})
      }

  const removePersistedCommentFromStorage = () => {
    debouncer.cancel()
    removeFromStorage()
  }

  return {
    hasPersistedComment,
    persistCommentToStorage,
    removePersistedCommentFromStorage,
  }
}

/**
 * Hook that returns whether a persisted pull request review comment exists
 * and functions to persist and remove pull request review comment data from local storage
 *
 */
export function usePersistedDiffCommentData({
  diffSide,
  filePath,
  handlePersistedCommentExists,
  line,
  subjectId,
  threadId,
  fileLevelComment,
}: {
  diffSide?: DiffSide
  filePath: string
  line?: number
  subjectId: string
  threadId?: string
  handlePersistedCommentExists?: (comment: PersistedDiffComment) => void
  fileLevelComment: boolean
}) {
  const persistedCommentLocalStorageKey = useMemo(() => {
    if (threadId) return threadId
    return threadId ?? buildKey({diffSide, filePath, line, subjectId}, fileLevelComment)
  }, [diffSide, filePath, line, subjectId, threadId, fileLevelComment])

  const {hasPersistedComment, removePersistedCommentFromStorage: removeFromStorage} =
    usePersistedCommentDataBase<PersistedDiffComment>(persistedCommentLocalStorageKey, handlePersistedCommentExists)

  const writePersistedDiffComment = (args: PersistedCommentArgs) => {
    let comment: PersistedCommentArgs = {text: args.text}
    if (args.startLine && args.startSide) {
      comment = {...comment, startLine: args.startLine, startSide: args.startSide}
    }
    setItem(persistedCommentLocalStorageKey, comment)
  }

  const debouncer = useDebouncer(writePersistedDiffComment, {wait: PERSIST_DEBOUNCE_MS, onUnmount: d => d.flush()})

  const debouncePersistEnabled = isFeatureEnabled('pull_requests_debounce_comment_persist')

  const persistCommentToStorage = debouncePersistEnabled
    ? (args: PersistedCommentArgs) => debouncer.maybeExecute(args)
    : writePersistedDiffComment

  const removePersistedCommentFromStorage = () => {
    debouncer.cancel()
    removeFromStorage()
  }
  const persistCommentOnGivenLineToStorage = (args: {
    text: string
    givenLineDiffSide: DiffSide
    givenFilePath: string
    givenLine: number
    shouldBeFileLevelComment: boolean
    startLine?: number
    startLineKey?: DiffLineKey
    endLineKey?: DiffLineKey
  }) => {
    let comment: PersistedDiffComment = {shouldFocus: true}
    if (args.text) {
      comment = {...comment, text: args.text}
    }
    if (args.startLine) {
      comment = {
        ...comment,
        startLine: args.startLine,
        startSide: args.givenLineDiffSide,
        startLineKey: args.startLineKey,
        endLineKey: args.endLineKey,
      }
    }
    setItem(
      buildKey(
        {diffSide: args.givenLineDiffSide, filePath: args.givenFilePath, line: args.givenLine, subjectId},
        args.shouldBeFileLevelComment,
      ),
      comment,
    )
  }

  return {
    hasPersistedComment,
    persistCommentToStorage,
    persistCommentOnGivenLineToStorage,
    removePersistedCommentFromStorage,
  }
}
